import React from 'react';
import { useField, FieldProps } from 'formik';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Icons from 'components/Icons';
import { formatPercent, unFormatPercent } from 'utils';

interface StyleType {
  isSmall: boolean;
}

const useStyles = makeStyles({
  inputField: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormHelperText-root': {
      bottom: 'unset',
      top: ({ isSmall }: StyleType) => (isSmall ? '2rem' : '3rem'),
      marginLeft: 0
    }
  },
  icon: {
    width: '24px',
    height: '24px'
  },
  iconBox: {
    position: 'absolute',
    top: 4,
    right: 0,
    width: 'max-content',
    height: 'max-content'
  },
  readOnly: {
    '& div': {
      border: 0,
      color: 'black !important',
      fontWeight: 400,
      '&:before': {
        border: 0
      }
    }
  },
  label: {
    marginBottom: '0.75rem',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  info: {
    position: 'absolute',
    right: 0,
    top: '12px',
    fontSize: '0.75rem',
    color: '#0000008a',
    fontWeight: 400,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '80%'
  }
});

interface TextProp extends FieldProps {
  label: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  shrink?: boolean;
  readOnly?: boolean;
  tooltip?: string;
  info?: string;
  width: string;
  labelOnTop?: boolean;
  onChange?: ({
    formatedValue,
    unFormattedValue
  }: {
    formatedValue: string;
    unFormattedValue: string;
  }) => void;
}

const Percentage = ({
  field,
  label,
  placeholder,
  readOnly,
  shrink,
  tooltip,
  width,
  labelOnTop,
  onChange,
  info,
  size = 'small',
  ...props
}: TextProp) => {
  const [, meta, helpers] = useField(field.name);
  const classes = useStyles({ isSmall: size === 'small' });

  const normalizeValue = (val: any) => {
    if (!val) return '';
    const replaceComaWithDot = val.replace(',', '.');
    const onlyNumber = replaceComaWithDot.replace(/[A-Za-z]/g, '');

    return onlyNumber === '%' ? '0%' : onlyNumber;
  };

  field.onBlur = () => {
    const { value } = field;
    if (value === '-') return helpers.setValue('-');

    const normalizedValue = normalizeValue(value);

    if (!value || normalizedValue.length === 0)
      return helpers.setValue(formatPercent(0));

    const unFormatted = unFormatPercent(normalizedValue);
    const formated = formatPercent(unFormatted);
    helpers.setValue(formated);

    if (onChange) {
      onChange({ formatedValue: formated, unFormattedValue: unFormatted });
    }
  };
  const { error, touched } = meta;
  const isError = typeof error === 'string' && touched;
  return (
    <div className={classes.inputField} style={{ width: width ? width : '' }}>
      {info && <span className={classes.info}>{info}</span>}
      {labelOnTop && label && (
        <label
          style={{ color: isError ? 'red' : 'inherit' }}
          className={classes.label}
        >
          {label}
        </label>
      )}
      <TextField
        label={!labelOnTop && label}
        style={{ width: '100%' }}
        className={readOnly ? classes.readOnly : ''}
        placeholder={placeholder}
        InputLabelProps={{
          shrink: shrink
        }}
        disabled={readOnly}
        error={isError}
        size={size}
        {...props}
        {...field}
        onFocus={() => {
          const emptyValue = '0.00%';
          if (field.value === emptyValue) {
            helpers.setValue('');
          }
        }}
        value={normalizeValue(field.value)}
      />
      <div className={classes.iconBox}>
        {isError && (
          <Tooltip title={error || ''}>
            <ErrorOutlineIcon
              className={classes.icon}
              style={{ color: 'red' }}
            />
          </Tooltip>
        )}
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <Icons.HelpIcon className={classes.icon} />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default Percentage;
